import { useEffect, useMemo, useState } from 'react'

import { useI18n } from '../../../../shared/i18n/useI18n.js'
import { useProductStrings } from '../../../../shared/i18n/useProductStrings.js'

import {
  COLOR_MODE,
  COLOR_MODE_REGISTRY,
} from '../../../../shared/constants/colorMode.js'

import {
  buildColorModeLegendItems,
  encodeColorModeLegendHoverValue,
  getColorModeFilterActive,
  getNextColorModeFiltersConfig,
  isColorModeLegendKeyActive,
  resolveColorModeHoveredLegendKey,
} from '../../../catalog/utils/colorModeLegend.js'


import { Close } from '../../../../shared/ui/icons/Close.jsx'
import { FiltersAlert } from '../../../../shared/ui/icons/FiltersAlert.jsx'
import { Select } from '../../../../shared/ui/Select/index.js'
import { QuestionMarkBubble } from '../../../../shared/ui/icons/QuestionMarkBubble.jsx'

import styles from './LayerLegendPanel.module.scss'

const PANEL_CLOSE_MS = 180

const LEGEND_COLUMNS_BY_MODE = {
  [COLOR_MODE.ROOMS]: 5,
  [COLOR_MODE.STATUS]: 3,
  [COLOR_MODE.INSOLATION]: 6,
  [COLOR_MODE.COMPARE]: 5,
  [COLOR_MODE.CATEGORY]: 4,
}

function getLegendColumns(colorMode, itemsCount) {
  const preferred = LEGEND_COLUMNS_BY_MODE[colorMode] ?? itemsCount
  return Math.max(1, Math.min(preferred, itemsCount || 1))
}

export function LayerLegendPanel({
  isVisible,
  isOpen,
  showToggle,
  onOpen,
  onClose,

  
  activeFiltersConfig = null,
  activeFiltersCount = 0,
  availableColorModes = [],
  colorMode,
  colorModeDeciles = null,
  onColorModeChange,
  defaultFiltersConfig = null,
  // hoveredProduct = null,
  legendContainerRef,
  onLegendHover,
  onShowFilters,
  products = [],
  setActiveFiltersConfig,
  showSoldHighlightNote = false,
}) {
  const { t, plural } = useI18n()
  const getProductsCategoryString = useProductStrings()

  const [localHoveredKey, setLocalHoveredKey] = useState(null)
  const [isPanelRendered, setIsPanelRendered] = useState(isOpen)

  const modeConfig = COLOR_MODE_REGISTRY[colorMode] ?? null
  const { filterId, toFilterValue, matchesFilterValue } = modeConfig ?? {}

// TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util?------------------------------------------------------------------------------------------------
// const modeOptions = useMemo(
//     () => Object.entries(COLOR_MODE_REGISTRY)
//     .filter(([value]) => !availableColorModes || availableColorModes.includes(value))
//     .map(([value, cfg]) => ({
//       value,
//       label: t(cfg.labelKey),
//     })),
//     [t, availableColorModes],
//   )
const modeOptions = useMemo(
  () => (availableColorModes || Object.keys(COLOR_MODE_REGISTRY))
    .map((value) => {
      const cfg = COLOR_MODE_REGISTRY[value];
      if (!cfg) return null;
      return {
        value,
        cfg,
        label: t(cfg.labelKey),
      };
    })
    .filter(Boolean),
  [t, availableColorModes],
);  

// TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util?------------------------------------------------------------------------------------------------
  const legendItems = useMemo(() => {
    return buildColorModeLegendItems(modeConfig, products)
  }, [modeConfig, products])
  
// TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util?------------------------------------------------------------------------------------------------
  const modeFilterActive = getColorModeFilterActive(filterId, activeFiltersConfig, defaultFiltersConfig)

// TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util?------------------------------------------------------------------------------------------------
  // const hoveredLegendKey = useMemo(() => {
  //   return resolveColorModeHoveredLegendKey({
  //     hoveredProduct,
  //     colorMode,
  //     colorModeDeciles,
  //     modeConfig,
  //   })
  // }, [hoveredProduct, colorMode, colorModeDeciles, modeConfig])

  useEffect(() => {
    if (!isVisible) {
      setIsPanelRendered(false)
      setLocalHoveredKey(null)
      onLegendHover?.(null)
      return
    }
  }, [isVisible, onLegendHover])

  useEffect(() => {
    if (isOpen) {
      setIsPanelRendered(true)
      return undefined
    }

    if (!isPanelRendered) return undefined

    const timeoutId = window.setTimeout(() => {
      setIsPanelRendered(false)
    }, PANEL_CLOSE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [isOpen, isPanelRendered])

  useEffect(() => {
    if (!isOpen) return undefined

    function handleKeyDown(event) {
      if (event.key !== 'Escape') return
      onClose?.()
      // setLocalHoveredKey(null)
      onLegendHover?.(null)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, onLegendHover])

  useEffect(() => () => onLegendHover?.(null), [onLegendHover])

  if (!isVisible || !modeConfig || !legendItems.length) return null

  const legendColumns = getLegendColumns(colorMode, legendItems.length)
  const isClosing = isPanelRendered && !isOpen
  const showFilterAlert = isPanelRendered && activeFiltersCount > 0 && typeof onShowFilters === 'function'

  function isLegendKeyActive(key) {
    return isColorModeLegendKeyActive({
      legendKey: key,
      filterId,
      toFilterValue,
      matchesFilterValue,
      activeFiltersConfig,
      defaultFiltersConfig,
    })
  }

  function clearLegendHover() {
    setLocalHoveredKey(null)
    onLegendHover?.(null)
  }

  function handleLegendHover(key) {
    setLocalHoveredKey(key)
    onLegendHover?.(encodeColorModeLegendHoverValue(colorMode, key))
  }

  function handleLegendClick(legendKey) {
    if (!filterId || !toFilterValue || !setActiveFiltersConfig || !defaultFiltersConfig) return

    setActiveFiltersConfig((prev) => getNextColorModeFiltersConfig({
      activeFiltersConfig: prev,
      defaultFiltersConfig,
      filterId,
      toFilterValue,
      legendKey,
    }))
  }


// TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util? -------------------------------------------------------------------------------------------------
  function getLegendLabels(item) {
    const { key, labelKey, labelFn } = item

    if (colorMode === COLOR_MODE.ROOMS) {
      return {
        displayLabel: key === 0 ? t('tiers.rooms_service') : String(key),
        fullLabel: labelFn ? labelFn(t, plural, key) : String(key),
      }
    }

    if (colorMode === COLOR_MODE.INSOLATION) {
      return {
        displayLabel: key === 'null' ? '∅' : key === 10 ? '10h' : `${key}h+`,
        fullLabel: labelKey ? t(labelKey) : String(key),
      }
    }

    if (colorMode === COLOR_MODE.COMPARE) {
      const decile = Number(String(key).replace('p', '')) + 1
      return {
        displayLabel: `${decile * 10}%`,
        fullLabel: t(`product.compare_tooltip_${key}`, `${decile * 10}%`),
      }
    }

    if (colorMode === COLOR_MODE.CATEGORY) {
      const fullLabel = getProductsCategoryString(null, key, { count: 1 })
      return {
        displayLabel: fullLabel,
        fullLabel,
      }
    }

    const fullLabel = labelFn ? labelFn(t, plural, key) : t(labelKey)
    return {
      displayLabel: fullLabel,
      fullLabel,
    }
  }

  return (
    <div 
      className={styles.legendHolder}
      ref={legendContainerRef}
    >
      {isPanelRendered && (
        <div
          className={[styles.panel, isOpen ? styles.panelOpen : ''].filter(Boolean).join(' ')}
          style={{ '--legend-columns': String(legendColumns) }}
        >
          <div className={styles.panelHeader}>
            <span className={styles.legendTitle}>{t('tiers.legend_label')}</span>
          </div>

          <Select
            className={styles.modeSelect}
            value={colorMode}
            onChange={onColorModeChange}
            options={modeOptions}
            preferDropUp
          />

          <div className={styles.itemsGrid}>
            {legendItems.map(({ key, labelKey, labelFn, fill, stroke, hoverFill, hoverOpacity }) => {
              const isActive = isLegendKeyActive(key)
              const isOff = modeFilterActive && !isActive
              // const isHighlighted = hoveredLegendKey === key || localHoveredKey === key
              const isHighlighted = localHoveredKey === key
              const { displayLabel, fullLabel } = getLegendLabels({ key, labelKey, labelFn })
              const swatchBg = isOff ? 'transparent' : (isHighlighted && hoverFill ? hoverFill : fill)

              return (
                <button
                  key={key}
                  data-legend-key={encodeColorModeLegendHoverValue(colorMode, key)}
                  type="button"
                  className={[
                    styles.item,
                    isOff ? styles.itemOff : '',
                    isActive ? styles.itemActive : '',
                    isHighlighted ? styles.itemHighlight : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleLegendClick(key)}
                  onMouseEnter={() => handleLegendHover(key)}
                  onMouseLeave={clearLegendHover}
                  onFocus={() => handleLegendHover(key)}
                  onBlur={clearLegendHover}
                  title={fullLabel}
                >
                  <span
                    className={styles.swatch}
                    style={{
                      backgroundColor: swatchBg,
                      borderColor: stroke,
                      opacity: isHighlighted && hoverOpacity ? hoverOpacity : undefined,
                    }}
                  />
                  <span className={styles.itemLabel}>{displayLabel}</span>
                </button>
              )
            })}
          </div>

          <div className={styles.panelFooter}>
            {showSoldHighlightNote
              ? <span className={styles.soldBadge}>{t('status.sold')}</span>
              : <span className={styles.footerSpacer} aria-hidden="true" />}

            <div className={styles.legendActions}>
              {showFilterAlert && (
                <button
                  type="button"
                  className={styles.filterAlertButton}
                  onClick={onShowFilters}
                  aria-label={t('common.filter')}
                  title={t('common.filter')}
                >
                  <FiltersAlert className={styles.cornerIcon} />
                  <span className={styles.filterAlertMark} aria-hidden="true">!</span>
                </button>
              )}

              <button
                type="button"
                className={[
                  styles.anchorButton,
                  styles.anchorButtonActive,
                  isClosing ? styles.anchorButtonClosing : '',
                ].filter(Boolean).join(' ')}
                onClick={onClose}
                aria-label={t('common.close')}
                aria-expanded={isOpen}
              >
                <Close className={styles.cornerIcon} />
              </button>
            </div>
          </div>
        </div>
      )}

      {!isPanelRendered && showToggle && (
        <button
          type="button"
          className={[styles.anchorButton, styles.standaloneAnchorButton].join(' ')}
          onClick={onOpen}
          aria-label={t('tiers.legend_label')}
          aria-expanded={isOpen}
        >
          <QuestionMarkBubble className={styles.cornerIcon} />
        </button>
      )}
    </div>
  )
}